import React, { useEffect, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useSubscription } from '../../hooks/useSubscription'
import './SubscriptionPage.css'

const SubscriptionPage = ({ onClose }) => {
  const { user } = useAuth()
  const { subscriptionStatus } = useSubscription()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Simuler un petit délai de chargement pour une meilleure UX
    const timer = setTimeout(() => {
      setIsLoading(false)
    }, 500)
    return () => clearTimeout(timer)
  }, [])

  // Si l'utilisateur est déjà abonné, fermer la page
  useEffect(() => {
    if (subscriptionStatus?.user_status === 'subscribed') {
      onClose()
    }
  }, [subscriptionStatus, onClose])

  const handleStripeRedirect = (event, stripeLink, planName) => {
    event.preventDefault()
    event.stopPropagation()
    
    console.log(`🔄 Clic sur le bouton ${planName}`)
    console.log('📧 Email utilisateur:', user?.email)
    console.log('🔗 URL Stripe originale:', stripeLink)
    
    // Ajouter l'email de l'utilisateur à l'URL Stripe pour pré-remplir le champ
    let finalStripeLink = stripeLink
    if (user?.email) {
      const encodedEmail = encodeURIComponent(user.email)
      const separator = stripeLink.includes('?') ? '&' : '?'
      finalStripeLink = `${stripeLink}${separator}prefilled_email=${encodedEmail}`
      console.log('✅ Email ajouté à l\'URL')
    } else {
      console.log('⚠️ Aucun email utilisateur trouvé')
    }
    
    console.log('🚀 Redirection vers Stripe:', finalStripeLink)
    
    // Petit délai pour s'assurer que les logs sont visibles
    setTimeout(() => {
      window.location.href = finalStripeLink
    }, 100)
  }

  const handleLegalPageOpen = (event, pageUrl, pageName) => {
    // Ne pas empêcher le comportement par défaut du lien
    // Juste stopper la propagation pour éviter la fermeture de la popup
    event.stopPropagation()
    
    console.log(`📄 Clic sur: ${pageName}`)
    console.log('🔗 URL:', pageUrl)
    console.log('✅ Ouverture via lien natif')
  }

  const getTrialMessage = () => {
    if (!subscriptionStatus) return ''
    
    if (subscriptionStatus.user_status === 'trial') {
      return `Il vous reste ${subscriptionStatus.trial_days_remaining} jour(s) d'essai gratuit`
    } else if (subscriptionStatus.user_status === 'expired') {
      return 'Votre période d\'essai est terminée'
    }
    return ''
  }

  const plans = {
    monthly: {
      name: 'Mensuel',
      price: '10€',
      period: '/mois',
      stripeLink: 'https://buy.stripe.com/eVq4gz6ta9Dx0QKevHffy00',
      features: [
        'Accès illimité à l\'application EMDR',
        'Personnalisation complète des paramètres',
        'Support client prioritaire',
        'Mises à jour automatiques',
        'Pas d\'engagement'
      ]
    },
    yearly: {
      name: 'Annuel',
      price: '100€',
      period: '/an',
      discount: 'Économisez 2 mois !',
      stripeLink: 'https://buy.stripe.com/9B600j3gY3f9eHAgDPffy01',
      features: [
        'Accès illimité à l\'application EMDR',
        'Personnalisation complète des paramètres',
        'Support client prioritaire',
        'Mises à jour automatiques',
        '2 mois gratuits inclus'
      ]
    }
  }

  if (isLoading) {
    return (
      <div className="subscription-page-overlay">
        <div className="subscription-page">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Chargement...</p>
          </div>
        </div>
      </div>
    )
  }

  const handleOverlayClick = (event) => {
    // Fermer seulement si on clique sur l'overlay lui-même, pas sur le contenu
    if (event.target === event.currentTarget) {
      onClose()
    }
  }

  return (
    <div className="subscription-page-overlay" onClick={handleOverlayClick}>
      <div className="subscription-page" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>×</button>
        
        <div className="subscription-header">
          <h1>Choisissez votre abonnement EMDR</h1>
          <p>Accédez à toutes les fonctionnalités premium pour optimiser vos séances thérapeutiques.</p>
        </div>

        {subscriptionStatus && (
          <div className="trial-status">
            <p className={`trial-message ${subscriptionStatus.user_status}`}>
              {getTrialMessage()}
            </p>
          </div>
        )}

        <div className="features-highlight">
          <h3>✨ Fonctionnalités Premium</h3>
          <div className="features-grid">
            <div className="feature-item">🎯 Personnalisation complète</div>
            <div className="feature-item">🎵 Sons thérapeutiques</div>
            <div className="feature-item">🎮 Support Joy-Con</div>
            <div className="feature-item">📱 Interface responsive</div>
            <div className="feature-item">🔄 Mises à jour</div>
            <div className="feature-item">💬 Support prioritaire</div>
          </div>
        </div>

        <div className="plans-container">
          {Object.entries(plans).map(([planId, plan]) => (
            <div key={planId} className="plan-card">
              <div className="plan-header">
                <h3>{plan.name}</h3>
                {plan.discount && (
                  <span className="discount-badge">{plan.discount}</span>
                )}
              </div>
              
              <div className="plan-price">
                <span className="price">{plan.price}</span>
                <span className="period">{plan.period}</span>
              </div>

              <ul className="plan-features">
                {plan.features.map((feature, index) => (
                  <li key={index}>
                    <span className="checkmark">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>

              <button 
                className="btn-subscribe-plan"
                onClick={(e) => handleStripeRedirect(e, plan.stripeLink, plan.name)}
                data-plan={planId}
              >
                Choisir {plan.name} - {plan.price}{plan.period}
              </button>
            </div>
          ))}
        </div>

        <div className="legal-links">
          <p>En vous abonnant, vous acceptez nos conditions d'utilisation et notre politique de confidentialité.</p>
          <div className="legal-buttons">
            <a 
              href="/terms.html"
              target="_blank"
              rel="noopener noreferrer"
              className="legal-link subscription-legal-link"
              onClick={(e) => handleLegalPageOpen(e, '/terms.html', 'Conditions d\'utilisation')}
            >
              Conditions d'utilisation
            </a>
            <a 
              href="/privacy.html"
              target="_blank"
              rel="noopener noreferrer"
              className="legal-link subscription-legal-link"
              onClick={(e) => handleLegalPageOpen(e, '/privacy.html', 'Politique de confidentialité')}
            >
              Politique de confidentialité
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SubscriptionPage 